import { useEffect, useState } from 'react'

// ─── Invoice maths, persistence shape, print & WhatsApp output ────────────────
// Single source of truth for every invoice screen (Billing, Appointments, Patients).

export const GST_RATE = 18

export const round2 = n => Math.round((Number(n) || 0) * 100) / 100

export function fmtINR(n) {
  return '₹' + round2(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })
}

export function fmtInvDate(d) {
  if (!d) return '—'
  const iso = String(d).slice(0, 10)
  return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

export const escapeHTML = v => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#039;')

// Accepts rows shaped either { service, qty, rate } (Billing) or { name, qty, rate } (modals)
export function normalizeLines(rows) {
  return (rows || [])
    .map(r => {
      const name = (r.service || r.name || '').trim()
      const qty  = Number(r.qty) || 0
      const rate = Number(r.rate) || 0
      return { name, qty, rate, amount: round2(qty * rate) }
    })
    .filter(r => r.name)
}

export function paymentStatus(total, paid) {
  if (paid <= 0) return 'unpaid'
  if (paid >= total) return 'paid'
  return 'partial'
}

// subtotal → discount (flat or %) → GST on the discounted amount → total
export function calcInvoice({ lines, discountType, discountValue, gstEnabled, amountPaid }) {
  const items    = normalizeLines(lines)
  const subtotal = round2(items.reduce((s, r) => s + r.amount, 0))
  const isPct    = discountType === 'percent' || discountType === '%'
  const dv       = Math.max(0, Number(discountValue) || 0)
  const discount = round2(isPct ? subtotal * Math.min(dv, 100) / 100 : Math.min(dv, subtotal))
  const taxable  = round2(subtotal - discount)
  const gst      = gstEnabled ? round2(taxable * GST_RATE / 100) : 0
  const total    = round2(taxable + gst)
  const paid     = round2(Math.max(0, Number(amountPaid) || 0))
  const overpaid = paid > total
  const balance  = round2(Math.max(0, total - paid))
  const status   = paymentStatus(total, Math.min(paid, total))
  return { items, subtotal, discount, taxable, gst, total, paid, balance, status, overpaid, isPct, discountValue: dv }
}

export function overpaidMessage(calc) {
  return `Amount paid (${fmtINR(calc.paid)}) can't be more than the invoice total (${fmtINR(calc.total)}).`
}

// "Amount paid" follows the total until the user types in it.
export function useAmountPaid(total, { initial, manual: initiallyManual = false } = {}) {
  const [value, setValue]   = useState(initiallyManual ? String(initial ?? '') : String(round2(total)))
  const [manual, setManual] = useState(initiallyManual)
  useEffect(() => { if (!manual) setValue(String(round2(total))) }, [total, manual])
  return {
    value,
    manual,
    set: v => { setValue(v); setManual(true) },
    resetToTotal: () => setManual(false),
  }
}

// Columns written on insert/update. invoice_number is NEVER sent — a DB trigger assigns it.
// Both new (discount_amount, gst_amount) and legacy (discount, gst) columns carry the same values.
export function invoiceDbFields(calc, { date, paymentMode, notes } = {}) {
  const items = calc.items
  return {
    items:           items.map(r => ({ service: r.name, qty: r.qty, rate: r.rate, amount: r.amount })),
    services:        items.map(r => ({ name: r.name, qty: r.qty, rate: r.rate, amount: r.amount })),
    subtotal:        calc.subtotal,
    discount_type:   calc.isPct ? 'percent' : 'flat',
    discount_value:  calc.discountValue,
    discount:        calc.discount,
    discount_amount: calc.discount,
    gst_enabled:     calc.gst > 0,
    gst:             calc.gst,
    gst_amount:      calc.gst,
    total:           calc.total,
    amount_paid:     calc.paid,
    balance_due:     calc.balance,
    payment_status:  calc.status,
    status:          calc.status,
    ...(date        !== undefined ? { date, invoice_date: date } : {}),
    ...(paymentMode !== undefined ? { payment_mode: paymentMode } : {}),
    ...(notes       !== undefined ? { notes: notes || null } : {}),
  }
}

// ─── Reading stored invoices (old rows may use legacy columns / capitalised status) ──
export function invTotal(inv) { return Number(inv?.total) || 0 }

export function invPaid(inv) {
  const total = invTotal(inv)
  if (inv?.amount_paid != null) return Math.min(Math.max(0, Number(inv.amount_paid) || 0), total)
  return normStatus(inv) === 'paid' ? total : 0
}

export function invBalance(inv) {
  if (inv?.balance_due != null) return Math.max(0, Number(inv.balance_due) || 0)
  return Math.max(0, invTotal(inv) - invPaid(inv))
}

export function normStatus(inv) {
  const raw = String(inv?.payment_status || inv?.status || '').toLowerCase().trim()
  if (raw === 'paid') return 'paid'
  if (raw === 'partial' || raw === 'partially paid') return 'partial'
  if (raw === 'unpaid' || raw === 'pending') return 'unpaid'
  const total = invTotal(inv)
  const paid  = Number(inv?.amount_paid) || 0
  return paymentStatus(total, Math.min(paid, total))
}

export function invLines(inv) { return normalizeLines(inv?.items?.length ? inv.items : inv?.services) }
export function invDate(inv)  { return inv?.date || inv?.invoice_date || inv?.created_at?.slice(0, 10) || '' }
export function invDiscount(inv) { return Number(inv?.discount_amount ?? inv?.discount) || 0 }
export function invGst(inv)      { return Number(inv?.gst_amount ?? inv?.gst) || 0 }

// GSTIN is only printed once one has been entered in Settings (clinic isn't GST-registered yet)
export function getGstin() {
  try { return (JSON.parse(localStorage.getItem('md_settings') || '{}').clinicGST || '').trim() } catch { return '' }
}

const STATUS_PRINT = {
  paid:    { label: 'PAID',    bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  partial: { label: 'PARTIAL', bg: '#fffbeb', color: '#d97706', border: '#fde68a' },
  unpaid:  { label: 'UNPAID',  bg: '#fff1f2', color: '#dc2626', border: '#fecdd3' },
}

// Full printable invoice document. `inv` is a saved invoices row.
export function buildInvoicePrintHTML(inv, clinic, patient = {}) {
  const c        = clinic || {}
  const gstin    = getGstin()
  const lines    = invLines(inv)
  const discount = invDiscount(inv)
  const gst      = invGst(inv)
  const paid     = invPaid(inv)
  const balance  = invBalance(inv)
  const st       = STATUS_PRINT[normStatus(inv)]
  const patName  = inv.patient_name || patient.full_name || '—'
  const patPhone = inv.patient_phone || patient.phone || ''
  const contact  = [c.phone && `Ph: ${escapeHTML(c.phone)}`, c.email && escapeHTML(c.email)].filter(Boolean).join(' &nbsp;|&nbsp; ')
  const pctLabel = inv.discount_type === 'percent' && Number(inv.discount_value) ? ` (${escapeHTML(inv.discount_value)}%)` : ''

  const rows = lines.map(r => `
    <tr>
      <td>${escapeHTML(r.name)}</td>
      <td style="text-align:center">${r.qty}</td>
      <td style="text-align:right">${fmtINR(r.rate)}</td>
      <td style="text-align:right"><strong>${fmtINR(r.amount)}</strong></td>
    </tr>`).join('')

  return `<!DOCTYPE html>
<html><head><meta charset="UTF-8">
<title>Invoice ${escapeHTML(inv.invoice_number || '')}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Arial,sans-serif;max-width:780px;margin:0 auto;padding:32px;color:#1e293b;font-size:14px}
  .header{display:flex;justify-content:space-between;align-items:flex-start;gap:24px;padding-bottom:18px;border-bottom:3px solid #126868;margin-bottom:22px}
  .clinic-name{font-size:24px;font-weight:800;color:#126868}
  .tagline{font-size:12.5px;font-weight:400;color:#148080;margin-top:1px}
  .address{font-size:12px;color:#64748b;text-align:right;line-height:1.6;max-width:360px}
  .meta{display:flex;gap:14px;margin-bottom:22px}
  .meta-box{background:#f8fafc;border-radius:8px;padding:12px 16px;flex:1}
  .meta-label{font-size:11px;color:#64748b;font-weight:600;text-transform:uppercase;letter-spacing:.5px;margin-bottom:4px}
  .meta-val{font-size:14px;font-weight:700;color:#0f172a}
  table{width:100%;border-collapse:collapse;margin-bottom:20px}
  th{background:#0f5454;color:white;padding:10px 14px;text-align:left;font-size:13px}
  td{padding:10px 14px;border-bottom:1px solid #e2e8f0;font-size:13px}
  .totals{margin-left:auto;width:300px;margin-bottom:22px}
  .row{display:flex;justify-content:space-between;padding:5px 0;font-size:14px;color:#475569}
  .grand{display:flex;justify-content:space-between;padding:10px 0 4px;border-top:2px solid #126868;font-size:19px;font-weight:800;color:#126868}
  .pay{border-radius:8px;padding:14px 18px;display:flex;gap:40px;margin-bottom:22px;background:${balance > 0 ? '#fffbeb' : '#f0fdf4'};border:1px solid ${balance > 0 ? '#fde68a' : '#bbf7d0'}}
  .pay-label{font-size:11px;color:#64748b;font-weight:600;margin-bottom:4px}
  .pay-val{font-size:16px;font-weight:700}
  .badge{display:inline-block;padding:3px 14px;border-radius:20px;font-size:12px;font-weight:700;background:${st.bg};color:${st.color};border:1px solid ${st.border}}
  .footer{text-align:center;padding-top:18px;border-top:1px solid #e2e8f0;color:#94a3b8;font-size:12px;line-height:1.6}
  @media print{body{padding:16px}}
</style></head>
<body>
<div class="header">
  <div>
    <div class="clinic-name">${escapeHTML(c.name || '')}</div>
    ${c.tagline ? `<div class="tagline">${escapeHTML(c.tagline)}</div>` : ''}
    ${gstin ? `<div style="font-size:12px;color:#475569;margin-top:4px">GSTIN: ${escapeHTML(gstin)}</div>` : ''}
  </div>
  <div class="address">
    ${c.address ? escapeHTML(c.address) + '<br>' : ''}${contact}
  </div>
</div>

<div class="meta">
  <div class="meta-box"><div class="meta-label">Invoice No.</div><div class="meta-val">${escapeHTML(inv.invoice_number || '—')}</div></div>
  <div class="meta-box"><div class="meta-label">Date</div><div class="meta-val">${fmtInvDate(invDate(inv))}</div></div>
  <div class="meta-box"><div class="meta-label">Patient</div><div class="meta-val">${escapeHTML(patName)}</div>${patPhone ? `<div style="font-size:12px;color:#64748b;margin-top:2px">${escapeHTML(patPhone)}</div>` : ''}</div>
  <div class="meta-box"><div class="meta-label">Status</div><div style="margin-top:3px"><span class="badge">${st.label}</span></div></div>
</div>

<table>
  <thead><tr><th>Service</th><th style="text-align:center">Qty</th><th style="text-align:right">Rate</th><th style="text-align:right">Amount</th></tr></thead>
  <tbody>${rows}</tbody>
</table>

<div class="totals">
  <div class="row"><span>Subtotal</span><span>${fmtINR(inv.subtotal)}</span></div>
  ${discount > 0 ? `<div class="row"><span>Discount${pctLabel}</span><span>−${fmtINR(discount)}</span></div>` : ''}
  ${gst > 0 ? `<div class="row"><span>GST (${GST_RATE}%)</span><span>${fmtINR(gst)}</span></div>` : ''}
  <div class="grand"><span>Total</span><span>${fmtINR(inv.total)}</span></div>
</div>

<div class="pay">
  <div><div class="pay-label">Payment Mode</div><div class="pay-val">${escapeHTML(inv.payment_mode || '—')}</div></div>
  <div><div class="pay-label">Amount Paid</div><div class="pay-val" style="color:#16a34a">${fmtINR(paid)}</div></div>
  <div><div class="pay-label">Balance Due</div><div class="pay-val" style="color:${balance > 0 ? '#d97706' : '#16a34a'}">${fmtINR(balance)}</div></div>
</div>

${inv.notes ? `<div style="background:#f8fafc;border-radius:8px;padding:12px 16px;margin-bottom:20px;font-size:13px;color:#475569"><strong>Notes:</strong> ${escapeHTML(inv.notes)}</div>` : ''}

<div class="footer">
  Thank you for choosing ${escapeHTML(c.name || 'us')}.${c.phone ? ` For queries call ${escapeHTML(c.phone)}.` : ''}<br>
  This is a computer-generated invoice.
</div>
</body></html>`
}

export function printInvoice(inv, clinic, patient) {
  const win = window.open('', '_blank', 'width=820,height=960')
  if (!win) { alert('Please allow pop-ups to print the invoice.'); return }
  win.document.write(buildInvoicePrintHTML(inv, clinic, patient))
  win.document.close()
  setTimeout(() => win.print(), 400)
}

export function buildInvoiceWhatsApp(inv, clinic, patient = {}) {
  const c        = clinic || {}
  const discount = invDiscount(inv)
  const gst      = invGst(inv)
  const balance  = invBalance(inv)
  return [
    `*${c.name || 'Invoice'} — Invoice ${inv.invoice_number || ''}*`.trim(),
    c.tagline ? `_${c.tagline}_` : null,
    `Date: ${fmtInvDate(invDate(inv))}`,
    `Patient: ${inv.patient_name || patient.full_name || ''}`,
    ``,
    ...invLines(inv).map(r => `• ${r.name} × ${r.qty} — ${fmtINR(r.amount)}`),
    ``,
    `Subtotal: ${fmtINR(inv.subtotal)}`,
    discount > 0 ? `Discount: −${fmtINR(discount)}` : null,
    gst > 0 ? `GST (${GST_RATE}%): ${fmtINR(gst)}` : null,
    `*Total: ${fmtINR(inv.total)}*`,
    `Paid: ${fmtINR(invPaid(inv))}`,
    balance > 0 ? `*Balance Due: ${fmtINR(balance)}*` : `✅ Fully Paid`,
    ``,
    `Thank you for choosing ${c.name || 'us'}!${c.phone ? ` For queries call ${c.phone}.` : ''}`,
  ].filter(v => v !== null).join('\n')
}

export function sendInvoiceWhatsApp(inv, clinic, patient = {}) {
  const phone = String(inv.patient_phone || patient.phone || '').replace(/\D/g, '').slice(-10)
  if (!phone) { alert('No phone number on record for this patient.'); return }
  window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(buildInvoiceWhatsApp(inv, clinic, patient))}`, '_blank')
}
